import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { useToast } from '../../hooks/useToast';
import Modal from '../../components/Modal';
import RichTextEditor from '../../components/RichTextEditor';

interface Release {
  id: string;
  version: string;
  buildNumber: number;
  buildDate: Date;
  fileSize: number;
  fileName: string;
  downloadURL: string;
  releaseNotes: string;
  minAndroidVersion: string;
  packageName: string;
  isLatest: boolean;
  createdAt: Date;
  updatedAt: Date;
}

const ReleasesPage: React.FC = () => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState<Release | null>(null);
  const [uploading, setUploading] = useState(false);

  // Form state
  const [version, setVersion] = useState('');
  const [buildNumber, setBuildNumber] = useState('');
  const [minAndroidVersion, setMinAndroidVersion] = useState('9.0');
  const [packageName, setPackageName] = useState('com.fyp.deepfakedetection');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [apkFile, setApkFile] = useState<File | null>(null);
  const [isLatest, setIsLatest] = useState(false);

  const { showToast } = useToast();

  useEffect(() => {
    fetchReleases();
  }, []);

  const fetchReleases = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'releases'), orderBy('buildDate', 'desc'));
      const snapshot = await getDocs(q);

      const releasesData: Release[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          version: data.version,
          buildNumber: data.buildNumber,
          buildDate: data.buildDate.toDate(),
          fileSize: data.fileSize,
          fileName: data.fileName,
          downloadURL: data.downloadURL,
          releaseNotes: data.releaseNotes,
          minAndroidVersion: data.minAndroidVersion,
          packageName: data.packageName,
          isLatest: data.isLatest || false,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
      });

      setReleases(releasesData);
    } catch (error) {
      console.error('Error fetching releases:', error);
      showToast({ type: 'error', message: 'Failed to load releases' });
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingRelease(null);
    setVersion('');
    setBuildNumber('');
    setMinAndroidVersion('9.0');
    setPackageName('com.fyp.deepfakedetection');
    setReleaseNotes('');
    setApkFile(null);
    setIsLatest(false);
    setIsModalOpen(true);
  };

  const openEditModal = (release: Release) => {
    setEditingRelease(release);
    setVersion(release.version);
    setBuildNumber(release.buildNumber.toString());
    setMinAndroidVersion(release.minAndroidVersion);
    setPackageName(release.packageName);
    setReleaseNotes(release.releaseNotes);
    setApkFile(null);
    setIsLatest(release.isLatest);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!version || !buildNumber) {
      showToast({
        type: 'error',
        message: 'Please fill in all required fields',
      });
      return;
    }

    if (!editingRelease && !apkFile) {
      showToast({
        type: 'error',
        message: 'Please select an APK file to upload',
      });
      return;
    }

    try {
      setUploading(true);

      let downloadURL = editingRelease?.downloadURL || '';
      let fileName = editingRelease?.fileName || '';
      let fileSize = editingRelease?.fileSize || 0;

      // Upload APK if a new file is provided
      if (apkFile) {
        fileName = `deepfake-detection-v${version}.apk`;
        const storageRef = ref(storage, `releases/${fileName}`);

        const uploadResult = await uploadBytes(storageRef, apkFile);
        downloadURL = await getDownloadURL(uploadResult.ref);
        fileSize = apkFile.size;

        showToast({ type: 'success', message: 'APK uploaded successfully' });
      }

      const releaseData = {
        version: version.trim(),
        buildNumber: parseInt(buildNumber),
        buildDate: Timestamp.fromDate(new Date()),
        fileSize,
        fileName,
        downloadURL,
        releaseNotes: releaseNotes.trim(),
        minAndroidVersion: minAndroidVersion.trim(),
        packageName: packageName.trim(),
        isLatest,
        updatedAt: Timestamp.fromDate(new Date()),
      };

      if (editingRelease) {
        // Update existing release
        await updateDoc(doc(db, 'releases', editingRelease.id), releaseData);
        showToast({ type: 'success', message: 'Release updated successfully' });
      } else {
        // Create new release
        await addDoc(collection(db, 'releases'), {
          ...releaseData,
          createdAt: Timestamp.fromDate(new Date()),
        });
        showToast({ type: 'success', message: 'Release created successfully' });
      }

      // If this release is marked as latest, unmark others
      if (isLatest) {
        const q = query(collection(db, 'releases'));
        const snapshot = await getDocs(q);
        const updatePromises = snapshot.docs
          .filter((d) => d.id !== editingRelease?.id)
          .map((d) =>
            updateDoc(doc(db, 'releases', d.id), { isLatest: false })
          );
        await Promise.all(updatePromises);
      }

      setIsModalOpen(false);
      fetchReleases();
    } catch (error) {
      console.error('Error saving release:', error);
      showToast({ type: 'error', message: 'Failed to save release' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (release: Release) => {
    if (
      !confirm(`Are you sure you want to delete version ${release.version}?`)
    ) {
      return;
    }

    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'releases', release.id));

      // Delete from Storage
      try {
        const storageRef = ref(storage, `releases/${release.fileName}`);
        await deleteObject(storageRef);
      } catch (error) {
        console.warn('Could not delete file from storage:', error);
      }

      showToast({ type: 'success', message: 'Release deleted successfully' });
      fetchReleases();
    } catch (error) {
      console.error('Error deleting release:', error);
      showToast({ type: 'error', message: 'Failed to delete release' });
    }
  };

  const handleSetLatest = async (release: Release) => {
    try {
      // Unmark all others
      const q = query(collection(db, 'releases'));
      const snapshot = await getDocs(q);
      const updatePromises = snapshot.docs.map((d) =>
        updateDoc(doc(db, 'releases', d.id), { isLatest: d.id === release.id })
      );
      await Promise.all(updatePromises);

      showToast({ type: 'success', message: 'Latest release updated' });
      fetchReleases();
    } catch (error) {
      console.error('Error setting latest release:', error);
      showToast({ type: 'error', message: 'Failed to update latest release' });
    }
  };

  const formatFileSize = (bytes: number): string => {
    const megabytes = bytes / (1024 * 1024);
    return `${megabytes.toFixed(1)} MB`;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading releases...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">App Releases</h2>
          <p className="text-gray-600 mt-1">
            Manage APK versions and downloads
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          New Release
        </button>
      </div>

      {/* Releases List */}
      <div className="space-y-4">
        {releases.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <svg
              className="w-16 h-16 mx-auto text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No releases yet
            </h3>
            <p className="text-gray-600 mb-4">
              Create your first APK release to get started
            </p>
            <button
              onClick={openCreateModal}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Create First Release
            </button>
          </div>
        ) : (
          releases.map((release) => (
            <div
              key={release.id}
              className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Version {release.version}
                    </h3>
                    {release.isLatest && (
                      <span className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                        LATEST
                      </span>
                    )}
                    <span className="bg-gray-100 text-gray-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                      Build {release.buildNumber}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                    <div>
                      <span className="text-gray-600">File Size:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {formatFileSize(release.fileSize)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Build Date:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {formatDate(release.buildDate)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Min Android:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {release.minAndroidVersion}+
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Package:</span>
                      <span className="ml-2 font-medium text-gray-900 text-xs">
                        {release.packageName}
                      </span>
                    </div>
                  </div>

                  {release.releaseNotes && (
                    <div className="text-sm text-gray-700 mb-3">
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: release.releaseNotes,
                        }}
                      />
                    </div>
                  )}

                  <div className="text-xs text-gray-500">
                    Created: {formatDate(release.createdAt)} | Updated:{' '}
                    {formatDate(release.updatedAt)}
                  </div>
                </div>

                <div className="flex flex-col gap-2 ml-4">
                  <button
                    onClick={() => openEditModal(release)}
                    className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-200 transition text-sm"
                  >
                    Edit
                  </button>
                  {!release.isLatest && (
                    <button
                      onClick={() => handleSetLatest(release)}
                      className="bg-green-100 text-green-700 px-3 py-1.5 rounded hover:bg-green-200 transition text-sm"
                    >
                      Set Latest
                    </button>
                  )}
                  <a
                    href={release.downloadURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded hover:bg-blue-200 transition text-sm text-center"
                  >
                    Download
                  </a>
                  <button
                    onClick={() => handleDelete(release)}
                    className="bg-red-100 text-red-700 px-3 py-1.5 rounded hover:bg-red-200 transition text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingRelease ? 'Edit Release' : 'Create New Release'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Version *
              </label>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="1.0.0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Build Number *
              </label>
              <input
                type="number"
                value={buildNumber}
                onChange={(e) => setBuildNumber(e.target.value)}
                placeholder="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Android Version
              </label>
              <input
                type="text"
                value={minAndroidVersion}
                onChange={(e) => setMinAndroidVersion(e.target.value)}
                placeholder="9.0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Package Name
              </label>
              <input
                type="text"
                value={packageName}
                onChange={(e) => setPackageName(e.target.value)}
                placeholder="com.fyp.deepfakedetection"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              APK File {!editingRelease && '*'}
            </label>
            <input
              type="file"
              accept=".apk"
              onChange={(e) => setApkFile(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {apkFile && (
              <p className="text-sm text-gray-600 mt-1">
                {apkFile.name} ({formatFileSize(apkFile.size)})
              </p>
            )}
            {editingRelease && !apkFile && (
              <p className="text-sm text-gray-600 mt-1">
                Current: {editingRelease.fileName} (
                {formatFileSize(editingRelease.fileSize)})
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Release Notes
            </label>
            <RichTextEditor value={releaseNotes} onChange={setReleaseNotes} />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isLatest"
              checked={isLatest}
              onChange={(e) => setIsLatest(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="isLatest" className="ml-2 text-sm text-gray-700">
              Mark as latest release (shown on download page)
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              disabled={uploading}
            >
              {uploading
                ? 'Saving...'
                : editingRelease
                  ? 'Update Release'
                  : 'Create Release'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ReleasesPage;
